import React, { Component, ReactNode } from "react"

interface ErrorBoundaryProps {
	children: ReactNode
	fallback?: ReactNode
	onError?: (error: Error, info: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
	hasError: boolean
	error?: Error
}

/**
 * ErrorBoundary — captura erros de renderização e exibe um fallback amigável.
 * Use em torno de rotas, layouts, ou componentes de alto risco (como hooks complexos).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props)
		this.state = { hasError: false }
	}

	static getDerivedStateFromError(error: Error): ErrorBoundaryState {
		return { hasError: true, error }
	}

	componentDidCatch(error: Error, info: React.ErrorInfo) {
		console.error("💥 ErrorBoundary capturou um erro:", error, info)
		if (this.props.onError) this.props.onError(error, info)
	}

	handleRetry = () => {
		this.setState({ hasError: false, error: undefined })
		window.location.reload()
	}

	render() {
		if (this.state.hasError) {
			if (this.props.fallback) {
				return this.props.fallback
			}

			return (
				<div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 text-center p-6">
					<h1 className="text-2xl font-semibold text-gray-800 mb-2">Ocorreu um erro inesperado</h1>
					<p className="text-gray-600 mb-4 max-w-md">Algo deu errado durante o carregamento desta página.</p>
					{this.state.error && (
						<pre className="text-sm text-red-500 bg-red-50 p-3 rounded-md max-w-md overflow-auto mb-4">
							{this.state.error.message}
						</pre>
					)}
					<button
            type="button"
						onClick={this.handleRetry}
						className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition">
						Recarregar página
					</button>
				</div>
			)
		}

		return this.props.children
	}
}
